import {Button, InfoTooltip, Pill, SearchField} from '@wandb/components';
import React, {useCallback, useMemo, useState} from 'react';
import {Virtuoso} from 'react-virtuoso';
import {twMerge} from 'tailwind-merge';

import {
  type PanelQuickAddEvent,
  trackPanelQuickAdd,
} from '../../../services/analytics/panelQuickAddEvents';
import {useDispatch} from '../../../state/hooks';
import * as PanelBankConfigActions from '../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {getDefaultPanelConfig} from '../../../util/panelbank';
import {useIsNgwEnabled} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {type AddPanel} from '../../PanelBank/types';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useSortAlphabetically} from '../Settings/hooks';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {AddBulkButton} from './AddBulkButton';
import {
  BULK_ADD_LIMIT_SINGLE_KEY,
  NEXT_GEN_KEYS_PAGE_SIZE,
  PAGE_SIZE,
} from './constants';
import {FoundPanelsPill} from './FoundPanelsPill';
import {useNextGenQuickAddKeys, useQuickAddKeys} from './hooks/useQuickAddKeys';
import {KeyTypeIcon} from './KeyTypeIcon';
import {useVirtuosoComponents} from './virtuosoComponents';

type SingleKeyPanelAddProps = {
  searchStr: string;
  dSearchStr: string;
  setSearchStr: (value: string) => void;
};

export const SingleKeyPanelAdd = ({
  searchStr,
  dSearchStr,
  setSearchStr,
}: SingleKeyPanelAddProps) => {
  const isNextGenAddPanelsDrawer = useIsNgwEnabled();

  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {targetSectionRef} = useWorkspaceDrawerContext();
  const isGlobalQuickAdd = !targetSectionRef;

  const sortAlphabetically = useSortAlphabetically();

  const trackQuickAddBound = useCallback(
    (event: Omit<PanelQuickAddEvent, 'scope'>) => {
      trackPanelQuickAdd({
        scope: isGlobalQuickAdd ? 'global' : 'section',
        isSearchActive: !!searchStr,
        ...event,
      });
    },
    [isGlobalQuickAdd, searchStr]
  );

  const dispatch = useDispatch();
  const onAddOne = useCallback(
    (key: string, spec: AddPanel['spec']) => {
      trackQuickAddBound({action: 'Add one clicked'});
      if (targetSectionRef) {
        dispatch(
          PanelBankSectionConfigActions.addPanelByConfig(
            targetSectionRef,
            getDefaultPanelConfig(key, spec),
            {
              shouldAlphaInsert: sortAlphabetically,
              panelBankConfigRef,
            }
          )
        );
      } else {
        dispatch(
          PanelBankConfigActions.addPanelsBySpec(
            panelBankConfigRef,
            [{key, spec}],
            sortAlphabetically
          )
        );
      }
    },
    [
      dispatch,
      panelBankConfigRef,
      sortAlphabetically,
      targetSectionRef,
      trackQuickAddBound,
    ]
  );

  return (
    <div className="h-full flex flex-col">
      {isNextGenAddPanelsDrawer ? (
        <NextGenSingleKeyPanelList
          searchStr={dSearchStr}
          inputSearchStr={searchStr}
          setSearchStr={setSearchStr}
          onAddOne={onAddOne}
          isGlobalQuickAdd={isGlobalQuickAdd}
          trackQuickAddBound={trackQuickAddBound}
        />
      ) : (
        <>
          <div className="px-24 py-16" data-dd-privacy={DD_PRIVACY_ALLOW}>
            <SearchField
              fullWidth
              aria-label="Search keys with regex"
              placeholder="Search keys with regex"
              value={searchStr}
              onChange={value => setSearchStr(value)}
              onBlur={() => trackQuickAddBound({action: 'Search box blurred'})}
            />
          </div>
          <SingleKeyPanelList
            searchStr={dSearchStr}
            onAddOne={onAddOne}
            isGlobalQuickAdd={isGlobalQuickAdd}
            trackQuickAddBound={trackQuickAddBound}
          />
        </>
      )}
    </div>
  );
};

type SingleKeyPanelListSharedProps = {
  searchStr: string;
  onAddOne: (key: string, spec: AddPanel['spec']) => void;
  isGlobalQuickAdd: boolean;
  trackQuickAddBound: (event: Omit<PanelQuickAddEvent, 'scope'>) => void;
};

type NextGenSingleKeyPanelListProps = SingleKeyPanelListSharedProps & {
  inputSearchStr: string;
  setSearchStr: (value: string) => void;
};

export const NextGenSingleKeyPanelList = ({
  searchStr,
  inputSearchStr,
  setSearchStr,
  onAddOne,
  isGlobalQuickAdd,
  trackQuickAddBound,
}: NextGenSingleKeyPanelListProps) => {
  const {
    searchedKeys,
    hasNextPage,
    retryInitialFetch,
    isInitialLoading,
    initialError,
    searchErrorMessage,
    loadMoreError,
    existingPanels,
    keysWithNoPanels,
  } = useNextGenQuickAddKeys(searchStr, NEXT_GEN_KEYS_PAGE_SIZE);
  // The query is debounced. Do not leave the last rejection under newer text
  // while the next request is still pending.
  const displayedSearchError =
    inputSearchStr === searchStr ? searchErrorMessage : undefined;

  const numKeysInList = searchedKeys.length;
  const numKeysToAdd = keysWithNoPanels.length;
  const numKeysAdded = numKeysInList - numKeysToAdd;
  // arbitrary limit for bulk add
  const isLimitExceeded = numKeysToAdd > BULK_ADD_LIMIT_SINGLE_KEY;

  const isBulkAddAllowed =
    !isInitialLoading && numKeysToAdd > 0 && !isLimitExceeded;

  const {components: virtuosoComponents, context: virtuosoContext} =
    useVirtuosoComponents({
      isFooterShown: isBulkAddAllowed,
      isLoading: isInitialLoading,
      initialError: initialError != null,
      retryInitialFetch,
    });

  return (
    <>
      <div className="px-24 py-16" data-dd-privacy={DD_PRIVACY_ALLOW}>
        <SearchField
          fullWidth
          aria-label="Search keys with regex"
          placeholder="Search keys with regex"
          value={inputSearchStr}
          onChange={setSearchStr}
          onBlur={() => trackQuickAddBound({action: 'Search box blurred'})}
          isInvalid={displayedSearchError != null}
          errorMessage={displayedSearchError}
        />
      </div>
      <SingleKeyPanelListHeader
        searchStr={searchStr}
        numKeysInList={numKeysInList}
        numKeysAdded={numKeysAdded}
        isLimitExceeded={isLimitExceeded}
        isNextGen
        isLoading={isInitialLoading}
      />

      <Virtuoso
        key={searchStr}
        components={virtuosoComponents}
        context={virtuosoContext}
        data={isInitialLoading ? [] : searchedKeys}
        increaseViewportBy={{top: 0, bottom: 1000}}
        itemContent={(_, [key, spec]) => {
          const canAdd = !existingPanels.global.get(key)?.length;
          const isInScope = (existingPanels.inScope.get(key)?.length ?? 0) > 0;

          return (
            <SingleKeyPanelItem
              key={key}
              metricKey={key}
              spec={spec}
              canAdd={canAdd}
              isInScope={isInScope}
              isGlobalQuickAdd={isGlobalQuickAdd}
              onAddOne={onAddOne}
            />
          );
        }}
      />
      {!isInitialLoading && initialError == null && hasNextPage ? (
        <div className="flex min-h-48 shrink-0 items-center border-t border-solid border-wb-border px-12 py-8 text-sm text-wb-text-tertiary">
          Results are truncated to the first{' '}
          {NEXT_GEN_KEYS_PAGE_SIZE.toLocaleString()} keys. Use search to find
          more specific matches.
        </div>
      ) : null}
      {isBulkAddAllowed && (
        <div className="border-t border-solid border-wb-border p-16 flex space-between gap-12">
          <AddBulkButton
            searchedKeys={searchedKeys}
            keysWithNoPanels={keysWithNoPanels}
            trackQuickAddBound={trackQuickAddBound}
            disabled={isInitialLoading}
          />
        </div>
      )}
    </>
  );
};

export const SingleKeyPanelList = ({
  searchStr,
  onAddOne,
  isGlobalQuickAdd,
  trackQuickAddBound,
}: SingleKeyPanelListSharedProps) => {
  const {searchedKeys, existingPanels, keysWithNoPanels} =
    useQuickAddKeys(searchStr);

  const [numKeysDisplayed, setNumKeysDisplayed] = useState(PAGE_SIZE);
  const displayedKeys = useMemo(
    () => searchedKeys.slice(0, numKeysDisplayed),
    [numKeysDisplayed, searchedKeys]
  );
  const showMoreKeys = useCallback(() => {
    // must check that there are actually more keys to display, otherwise
    // we'll be stuck in an infinite setState loop at the bottom of the list
    if (searchedKeys.length > displayedKeys.length) {
      setNumKeysDisplayed(prev => prev + PAGE_SIZE);
    }
  }, [displayedKeys.length, searchedKeys.length]);

  const numKeysInList = searchedKeys.length;
  const numKeysToAdd = keysWithNoPanels.length;
  const numKeysAdded = numKeysInList - numKeysToAdd;
  const isLimitExceeded = numKeysToAdd > BULK_ADD_LIMIT_SINGLE_KEY;
  const isAllKeysAdded = numKeysInList > 0 && numKeysToAdd === 0;

  const isBulkAddAllowed =
    numKeysToAdd > 0 && !isLimitExceeded && !isAllKeysAdded;

  const {components: virtuosoComponents, context: virtuosoContext} =
    useVirtuosoComponents({isFooterShown: isBulkAddAllowed});

  return (
    <>
      <SingleKeyPanelListHeader
        searchStr={searchStr}
        numKeysInList={numKeysInList}
        numKeysAdded={numKeysAdded}
        isLimitExceeded={isLimitExceeded}
      />
      <Virtuoso
        components={virtuosoComponents}
        context={virtuosoContext}
        data={displayedKeys}
        endReached={showMoreKeys}
        increaseViewportBy={PAGE_SIZE}
        itemContent={(_, [key, spec]) => {
          const canAdd = !existingPanels.global.get(key)?.length;
          const isInScope = (existingPanels.inScope.get(key)?.length ?? 0) > 0;

          return (
            <SingleKeyPanelItem
              key={key}
              metricKey={key}
              spec={spec}
              canAdd={canAdd}
              isInScope={isInScope}
              isGlobalQuickAdd={isGlobalQuickAdd}
              onAddOne={onAddOne}
            />
          );
        }}
      />
      {isBulkAddAllowed && (
        <div className="border-t border-solid border-wb-border p-16 flex items-center gap-12">
          <AddBulkButton
            searchedKeys={searchedKeys}
            keysWithNoPanels={keysWithNoPanels}
            trackQuickAddBound={trackQuickAddBound}
          />
        </div>
      )}
    </>
  );
};

type SingleKeyPanelListHeaderProps = {
  searchStr: string;
  numKeysInList: number;
  numKeysAdded: number;
  isLimitExceeded: boolean;
  isNextGen?: boolean;
  isLoading?: boolean;
};
export const SingleKeyPanelListHeader = ({
  searchStr,
  numKeysInList,
  numKeysAdded,
  isLimitExceeded,
  isNextGen = false,
  isLoading = false,
}: SingleKeyPanelListHeaderProps) => {
  return (
    <div className="flex h-[48px] shrink-0 items-center justify-between gap-16 border-y border-solid border-wb-border bg-wb-surface-sunken px-24">
      <div className="flex items-center gap-8">
        <span className="text-sm font-semibold text-wb-text-tertiary">
          {searchStr ? 'RESULTS' : 'KEYS'}
        </span>
        {!isLoading && (
          <div
            className="rounded bg-oblivion/[0.03] px-4 text-xs font-semibold text-wb-text-tertiary"
            aria-describedby={
              isLimitExceeded ? 'bulk-add-limit-tooltip' : undefined
            }>
            {isNextGen
              ? `${numKeysInList.toLocaleString()} loaded`
              : numKeysInList.toLocaleString()}
          </div>
        )}
        {isLimitExceeded && (
          <InfoTooltip
            id="bulk-add-limit-tooltip"
            aria-label="Bulk add limit tooltip"
            size="sm">
            You may add up to {BULK_ADD_LIMIT_SINGLE_KEY.toLocaleString()} keys
            at once. Refine your search to limit the results.
          </InfoTooltip>
        )}
      </div>
      {!isLoading && numKeysAdded > 0 && (
        <Pill
          icon="checkmark2"
          textValue={`${numKeysAdded.toLocaleString()} added`}
          color="green"
        />
      )}
    </div>
  );
};

type SingleKeyPanelItemProps = {
  metricKey: string;
  spec: AddPanel['spec'];
  canAdd: boolean;
  isInScope: boolean;
  isGlobalQuickAdd: boolean;
  onAddOne: (key: string, spec: AddPanel['spec']) => void;
};
const SingleKeyPanelItem = ({
  metricKey,
  spec,
  canAdd,
  isInScope,
  isGlobalQuickAdd,
  onAddOne,
}: SingleKeyPanelItemProps) => {
  return (
    <div
      className={twMerge(
        'flex items-center justify-between gap-16 px-24 hover:bg-wb-surface-hover',
        isGlobalQuickAdd ? 'py-6' : 'py-10',
        '[&:hover_button[data-test="add-one-btn"]]:visible [&_button[data-test="add-one-btn"]]:invisible'
      )}>
      <div className="flex gap-8">
        <KeyTypeIcon
          keyType={spec.keyType}
          isApiAddedPanel={spec.type === 'api-added-panel'}
        />
        <div>
          <p className="break-all">
            {isGlobalQuickAdd && metricKey.startsWith(spec.defaultSection + '/')
              ? metricKey.slice(spec.defaultSection.length + 1)
              : metricKey}
          </p>
          {isGlobalQuickAdd && (
            <p className="break-all text-sm text-wb-text-tertiary">
              {spec.defaultSection}
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-8">
        {canAdd ? (
          <Button
            data-test="add-one-btn"
            variant="secondary"
            startIcon="add-new"
            size="sm"
            onPress={() => onAddOne(metricKey, spec)}>
            Add
          </Button>
        ) : (
          <FoundPanelsPill isInScope={isInScope} />
        )}
      </div>
    </div>
  );
};
